// Mock data for Find Clarity landing page

export const heroData = {
  headline: "Find Clarity. Live Peacefully.",
  subheading: "AI-powered guidance to help you think clearly, reduce mental overload, and make confident decisions about what matters most.",
  heroImage: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=1200&q=80",
  ctaButtons: [
    {
      text: "Download on App Store",
      link: "https://google.com",
      type: "primary",
      icon: "apple"
    },
    {
      text: "Get it on Google Play",
      link: "https://google.com",
      type: "secondary",
      icon: "play"
    }
  ]
};

export const statsData = [
  {
    id: 1,
    number: "50K+",
    label: "Active Users"
  },
  {
    id: 2,
    number: "4.8/5",
    label: "App Store Rating"
  },
  {
    id: 3,
    number: "100K+",
    label: "Decisions Made"
  },
  {
    id: 4,
    number: "24/7",
    label: "Available"
  }
];

export const howItWorksSteps = [
  {
    id: 1,
    title: "Ask What's on Your Mind",
    description: "Share your question, concern, or confusion freely. Career, relationships, finance, or life decisions.",
    icon: "message-circle"
  },
  {
    id: 2,
    title: "AI Organizes Your Thoughts",
    description: "Our intelligent system structures your thinking, breaks down complexity, and identifies what truly matters.",
    icon: "brain"
  },
  {
    id: 3,
    title: "Get Clear, Actionable Guidance",
    description: "Receive structured answers with clear next steps. No jargon. No confusion. Just clarity.",
    icon: "lightbulb"
  }
];

export const keyBenefits = [
  {
    id: 1,
    title: "Reduce Mental Confusion",
    description: "Transform overwhelming thoughts into organized, manageable insights.",
    icon: "shield-check"
  },
  {
    id: 2,
    title: "Think Clearly Under Pressure",
    description: "Stay focused and make rational decisions even when stressed or uncertain.",
    icon: "target"
  },
  {
    id: 3,
    title: "Make Better Decisions Faster",
    description: "Cut through noise and uncertainty with structured guidance and clear next steps.",
    icon: "zap"
  },
  {
    id: 4,
    title: "Always Available & Private",
    description: "Access clarity anytime, anywhere. Your conversations stay completely private.",
    icon: "lock"
  }
];

export const useCases = [
  {
    id: 1,
    title: "Career Decisions",
    description: "Should I switch jobs? How do I negotiate salary? What skills should I learn next?",
    icon: "briefcase",
    image: "https://images.pexels.com/photos/7413915/pexels-photo-7413915.jpeg?w=600&q=80"
  },
  {
    id: 2,
    title: "Business & Finance",
    description: "Investment choices, budget planning, business strategy, and financial decisions made clearer.",
    icon: "trending-up",
    image: "https://images.pexels.com/photos/7413915/pexels-photo-7413915.jpeg?w=600&q=80"
  },
  {
    id: 3,
    title: "Daily Life Clarity",
    description: "Relationships, time management, priorities, and everyday challenges simplified.",
    icon: "home",
    image: "https://images.pexels.com/photos/289998/pexels-photo-289998.jpeg?w=600&q=80"
  },
  {
    id: 4,
    title: "Personal Growth",
    description: "Build better habits, improve mindset, overcome self-doubt, and achieve your goals.",
    icon: "sparkles",
    image: "https://images.unsplash.com/photo-1618425977996-bebc5afe88f9?w=600&q=80"
  }
];

export const whyFindClarity = [
  {
    id: 1,
    title: "Clean, Distraction-Free Design",
    description: "Focus on what matters. No clutter, no ads, no unnecessary features."
  },
  {
    id: 2,
    title: "No Information Overload",
    description: "Get concise, relevant answers. We respect your time and attention."
  },
  {
    id: 3,
    title: "Focus on Clarity, Not Noise",
    description: "Designed to help you think better, not to keep you endlessly scrolling."
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Priya Sharma",
    role: "Product Manager, Bangalore",
    testimonial: "findClarity helped me think through a major career transition. The structured guidance was exactly what I needed when I felt overwhelmed.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80"
  },
  {
    id: 2,
    name: "Rahul Verma",
    role: "Startup Founder, Mumbai",
    testimonial: "As a founder, I face dozens of decisions daily. This app helps me cut through the noise and focus on what actually matters.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80"
  },
  {
    id: 3,
    name: "Ananya Krishnan",
    role: "Engineering Student, Chennai",
    testimonial: "I was stuck choosing between job offers. findClarity broke down my options so clearly that the right choice became obvious.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&q=80"
  },
  {
    id: 4,
    name: "Vikram Patel",
    role: "Financial Analyst, Delhi",
    testimonial: "Finally, an AI app that doesn't overwhelm me with information. It gives me exactly what I need to make confident decisions.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80"
  },
  {
    id: 5,
    name: "Meera Reddy",
    role: "Marketing Lead, Hyderabad",
    testimonial: "The clarity I get from this app has improved my decision-making quality significantly. It's like having a thinking partner.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&q=80"
  },
  {
    id: 6,
    name: "Arjun Mehta",
    role: "Freelance Designer, Pune",
    testimonial: "When I feel stuck or confused, findClarity helps me organize my thoughts and see the path forward. Simple and effective.",
    rating: 5,
    avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&q=80"
  }
];

export const faqs = [
  {
    id: 1,
    question: "What is Find Clarity?",
    answer: "Find Clarity is an AI-powered app that helps you think clearly and make better decisions. Ask any question about your career, life, relationships, or personal growth, and get structured, actionable guidance."
  },
  {
    id: 2,
    question: "Is Find Clarity really free?",
    answer: "Yes! Find Clarity is completely free to download and use. Our mission is to make clarity and better thinking accessible to everyone."
  },
  {
    id: 3,
    question: "How is my privacy protected?",
    answer: "Your conversations are private and secure. We use industry-standard encryption and never share your personal data with third parties. You can read our full Privacy Policy for details."
  },
  {
    id: 4,
    question: "What kind of questions can I ask?",
    answer: "Any question where you need clarity! Career decisions, business strategy, financial choices, relationship guidance, personal growth, daily life challenges, and more. If you're confused or stuck, Find Clarity can help."
  },
  {
    id: 5,
    question: "Is this a replacement for professional therapy or counseling?",
    answer: "No. Find Clarity is a thinking and decision-making tool, not a substitute for professional mental health services. For serious mental health concerns, please consult a licensed therapist or counselor."
  },
  {
    id: 6,
    question: "Which devices does it work on?",
    answer: "Find Clarity is available for iOS and Android devices. We're working on a web version too."
  },
  {
    id: 7,
    question: "How accurate is the AI guidance?",
    answer: "Our AI provides thoughtful, structured guidance based on your input. While it's designed to help you think clearly, always use your own judgment for important decisions."
  },
  {
    id: 8,
    question: "Can I use Find Clarity in languages other than English?",
    answer: "Currently, Find Clarity works best in English. We're actively working on adding support for more languages including Hindi and other Indian languages."
  }
];

export const footerData = {
  tagline: "Clarity for better decisions",
  supportEmail: "shivam@findclarity.app",
  links: [
    { text: "Privacy Policy", path: "/privacy" },
    { text: "Terms & Conditions", path: "/terms" },
    { text: "Contact", email: "shivam@findclarity.app" }
  ],
  copyright: `© ${new Date().getFullYear()} Find Clarity. All rights reserved.`
};
